import React, { useState, useCallback } from "react";
import { 
  SuccessMessage,
  FailMessage,
  Loading,
  CompanySelect,
  FormatDateToMDY,
  FormatValueToDecimal,
  DateSelect,
  ItemSelect,
  CategorySelect
} from "../../common/APICommon";
import { apiUrl } from "../../utils/Configuration";
import axios from 'axios';
import { PDFDocument  } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import NoAccess from "../NoAccess";
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

export default function StockCardReport() {
//#region Others
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
//#endregion






//#region Item 
  const [company, setCompany] = useState(null);
  const [item, setItem] = useState(null);
  const [categoryName, setCategoryName] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  
  const [showItemSelectForm, setShowItemSelectForm] = useState(false);
  const openItemSelectForm = () => {
    setShowItemSelectForm(true);
  }
  const closeItemSelectForm = () => {
    setShowItemSelectForm(false);
  }
//#endregion







//#region Generate
  const fetchReportData = useCallback(async () => {
    const body = {
      categoryName: categoryName || null,
      itemCode: item?.itemCode || null,
      dateFrom: dateFrom,
      dateTo: dateTo,
    }

    try {
      const response = await axios.post(`${apiUrl}/api/Report/report-data/stock-card-report`, body, {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null
    }
  }, [dateFrom, dateTo, item, categoryName]);

  const generateHandleClick = async () => {
    if (!company || !dateFrom || !dateTo) {
      setFailMessage("Please fill up required fields");
      return;
    }

    setLoading(true);

    try {
      const _data = await fetchReportData();

      if (!_data || _data.length === 0) {
        setFailMessage("No data found for the selected filter");
        setLoading(false);
        return;
      }

      // Ensure _data handles both nested bulkData [{ itemCode, data: [...] }] or flat arrays
      const itemGroups = Array.isArray(_data) && _data[0]?.data
        ? _data
        : [{ itemCode: "", itemDescription: "", data: _data }];

      // --- HELPER FORMATTERS ---
      const formatDate = (dateVal) => {
        if (!dateVal) return "";
        if (typeof FormatDateToMDY === "function") return FormatDateToMDY(dateVal);
        const d = new Date(dateVal);
        return isNaN(d.getTime()) ? String(dateVal) : `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
      };

      const formatDecimal = (val, decimals = 2) => {
        if (val === null || val === undefined || val === "") return "";
        if (typeof FormatValueToDecimal === "function") return FormatValueToDecimal(val);
        const num = Number(val);
        return isNaN(num) ? "" : num.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      };

      // --- LOAD TEMPLATE AND FONTS ---
      let pdfDoc;
      let templateDoc = null;

      try {
        const pdfUrl = `${window.location.origin}/docs/STOCK_CARD_REPORT.pdf`;
        const response = await fetch(pdfUrl);
        if (response.ok) {
          const pdfBytes = await response.arrayBuffer();
          pdfDoc = await PDFDocument.load(pdfBytes);
          templateDoc = await PDFDocument.load(pdfBytes);
        }
      } catch (e) {
        console.warn("Could not load template PDF, generating blank landscape page instead.", e);
      }

      // Default to US Letter Landscape (792 x 612 pt) if template is missing
      if (!pdfDoc) {
        pdfDoc = await PDFDocument.create();
        pdfDoc.addPage([792, 612]);
      }

      pdfDoc.registerFontkit(fontkit);
      const fontBytes = await (await fetch("/calibri.ttf")).arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);
      const boldBytes = await (await fetch("/calibrib.ttf")).arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      // --- LANDSCAPE COORDINATES (Width: 792, Height: 612) ---
      const COL_X_DOC_NO = 40;
      const COL_X_DATE = 102;
      const COL_X_CODE = 155;
      const COL_X_BEG_BAL_RIGHT = 242;     // Right-aligned
      const COL_X_IN_RIGHT = 290;          // Right-aligned
      const COL_X_OUT_RIGHT = 338;         // Right-aligned
      const COL_X_ENDING_BAL_RIGHT = 400;  // Right-aligned
      const COL_X_REMARKS = 408;           // Left-aligned
      const COL_X_UNIT_COST_RIGHT = 555;   // Right-aligned
      const COL_X_AMOUNT_RIGHT = 620;      // Right-aligned
      const COL_X_INV_TOTAL_RIGHT = 705;   // Right-aligned
      const COL_X_AVE_COST_RIGHT = 760;    // Right-aligned

      const ROW_HEIGHT = 12;
      const BOTTOM_THRESHOLD = 40;

      // Helper to right-align numeric text
      const drawTextRight = (targetPage, text, rightX, y, font, size) => {
        if (text === null || text === undefined || text === "") return;
        const textStr = text.toString();
        const textWidth = font.widthOfTextAtSize(textStr, size);
        targetPage.drawText(textStr, {
          x: rightX - textWidth,
          y,
          size,
          font,
        });
      };

      // Calculate Item filter range for header
      const firstItemName = itemGroups[0]?.data?.[0]?.itemDesciption || itemGroups[0]?.itemDescription || itemGroups[0]?.itemCode || "All";
      const lastGroup = itemGroups[itemGroups.length - 1];
      const lastItemName = lastGroup?.data?.[lastGroup.data.length - 1]?.itemDesciption || lastGroup?.itemDescription || lastGroup?.itemCode || firstItemName;
      const itemFilterRange = `${firstItemName} to ${lastItemName}`;

      // --- DRAW REUSABLE HEADER ---
      const drawHeader = (targetPage) => {
        const { height: PAGE_HEIGHT } = targetPage.getSize();

        // Company info
        targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: PAGE_HEIGHT - 70, size: 16, font: calibriBold });
        targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: PAGE_HEIGHT - 82, size: 8, font: calibriFont });
        
        // Title and Filters
        targetPage.drawText(`${itemFilterRange}`, { x: 60, y: PAGE_HEIGHT - 106, size: 8, font: calibriFont });
        targetPage.drawText(`${formatDate(dateFrom)} to ${formatDate(dateTo)}`, { x: 85, y: PAGE_HEIGHT - 118, size: 8, font: calibriFont });
        targetPage.drawText(`${formatDate(new Date())}    Page : `, { x: 85, y: PAGE_HEIGHT - 128, size: 8, font: calibriFont });
      };

      // Helper for pagination
      const addNewPage = async () => {
        if (templateDoc) {
          const [newPage] = await pdfDoc.copyPages(templateDoc, [0]);
          return pdfDoc.addPage(newPage);
        }
        return pdfDoc.addPage([792, 612]);
      };

      let currentPage = pdfDoc.getPages()[0];
      const { height: PAGE_HEIGHT } = currentPage.getSize();
      const TABLE_START_Y = PAGE_HEIGHT - 170;

      drawHeader(currentPage);
      let yPosition = TABLE_START_Y;
      // let currentCategory = null;

      // --- DRAW DATA ROWS ---
      for (const group of itemGroups) {
        // const categoryTitle = group.categoryName || group.data?.[0]?.categoryname || "All";
        const itemTitle = group.data?.[0]?.itemDesciption || group.itemDescription || group.itemCode || "";

        // Ensure space for category and item header
        if (yPosition < BOTTOM_THRESHOLD + ROW_HEIGHT * 2) {
          currentPage = await addNewPage();
          drawHeader(currentPage);
          yPosition = TABLE_START_Y;
        }

        // 1. Group / Category Header (e.g. "All")
        // if (currentCategory !== categoryTitle) {
        //   currentPage.drawText(categoryTitle, { x: 40, y: yPosition, size: 8, font: calibriBold });
        //   yPosition -= ROW_HEIGHT;
        //   currentCategory = categoryTitle;
        // }

        // 2. Indented Item Name Header
        currentPage.drawText(itemTitle, { x: 45, y: yPosition, size: 8, font: calibriBold });
        yPosition -= ROW_HEIGHT;

        // 3. Transactions under this item
        const transactions = group.data || [];
        for (let i = 0; i < transactions.length; i++) {
          const item = transactions[i];

          // Trigger new page if reaching the bottom threshold
          if (yPosition < BOTTOM_THRESHOLD) {
            currentPage = await addNewPage();
            drawHeader(currentPage);
            yPosition = TABLE_START_Y;
            // Re-print continuation label for context
            currentPage.drawText(`${itemTitle} (cont.)`, { x: 40, y: yPosition, size: 8, font: calibriBold });
            yPosition -= ROW_HEIGHT;
          }

          const docNoStr = (item.docNo || item.documentNumber || "")?.toString();
          const dateStr = item.date ? formatDate(item.date) : "";
          const codeStr = (item.code || "")?.toString();

          const begBalVal = item.beginningBalance;
          const inVal = Number(item.in) > 0 ? item.in : null;
          const outVal = Number(item.out) > 0 ? item.out : null;
          const endingBalVal = item.endingBalance;

          let remarksStr = (item.remarks || item.docRemarks || item.remaining || "")?.toString();
          const unitCostVal = item.unitCost;
          const amountVal = item.amount;
          const invTotalVal = item.inventoryTotal;
          const aveCostVal = item.averageCost;

          // Truncate Remarks if too long to prevent collision with Unit Cost
          const maxRemarksWidth = 140;
          if (calibriFont.widthOfTextAtSize(remarksStr, 8) > maxRemarksWidth) {
            while (remarksStr.length > 0 && calibriFont.widthOfTextAtSize(remarksStr + "...", 8) > maxRemarksWidth) {
              remarksStr = remarksStr.slice(0, -1);
            }
            remarksStr += "...";
          }

          // Draw Column Values
          currentPage.drawText(docNoStr, { x: COL_X_DOC_NO, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(dateStr, { x: COL_X_DATE, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(codeStr, { x: COL_X_CODE, y: yPosition, size: 8, font: calibriFont });

          if (begBalVal !== null && begBalVal !== undefined) {
            drawTextRight(currentPage, formatDecimal(begBalVal), COL_X_BEG_BAL_RIGHT, yPosition, calibriFont, 8);
          }
          if (inVal !== null && inVal !== undefined) {
            drawTextRight(currentPage, formatDecimal(inVal), COL_X_IN_RIGHT, yPosition, calibriFont, 8);
          }
          if (outVal !== null && outVal !== undefined) {
            drawTextRight(currentPage, formatDecimal(outVal), COL_X_OUT_RIGHT, yPosition, calibriFont, 8);
          }
          if (endingBalVal !== null && endingBalVal !== undefined) {
            drawTextRight(currentPage, formatDecimal(endingBalVal), COL_X_ENDING_BAL_RIGHT, yPosition, calibriFont, 8);
          }

          currentPage.drawText(remarksStr, { x: COL_X_REMARKS, y: yPosition, size: 8, font: calibriFont });

          if (unitCostVal !== null && unitCostVal !== undefined) {
            drawTextRight(currentPage, formatDecimal(unitCostVal), COL_X_UNIT_COST_RIGHT, yPosition, calibriFont, 8);
          }

          // Format negative sign for Out/Sale amounts
          if (amountVal !== null && amountVal !== undefined) {
            const isNegative = Number(outVal) > 0 || String(codeStr).toUpperCase().includes("SAL");
            const formattedAmount = isNegative
              ? `- ${formatDecimal(Math.abs(amountVal))}`
              : formatDecimal(amountVal);
            drawTextRight(currentPage, formattedAmount, COL_X_AMOUNT_RIGHT, yPosition, calibriFont, 8);
          }

          if (invTotalVal !== null && invTotalVal !== undefined && Number(invTotalVal) !== 0) {
            drawTextRight(currentPage, formatDecimal(invTotalVal), COL_X_INV_TOTAL_RIGHT, yPosition, calibriFont, 8);
          }

          if (aveCostVal !== null && aveCostVal !== undefined && Number(aveCostVal) !== 0) {
            drawTextRight(currentPage, formatDecimal(aveCostVal), COL_X_AVE_COST_RIGHT, yPosition, calibriFont, 8);
          }

          yPosition -= ROW_HEIGHT;
        }

        // Small spacing between items
        yPosition -= ROW_HEIGHT / 2;
      }

      // --- DRAW PAGE NUMBERS ---
      const allPages = pdfDoc.getPages();
      for (let i = 0; i < allPages.length; i++) {
        const page = allPages[i];
        const { height } = page.getSize();
        // Draw page number right next to "Page : " (X: 175, Y: height - 95)
        page.drawText(`${i + 1}`, {
          x: 155,
          y: height - 128,
          size: 8,
          font: calibriFont,
        });
      }

      // --- SAVE AND DOWNLOAD ---
      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `STOCK_CARD_REPORT_${formatDate(dateFrom)}_to_${formatDate(dateTo)}.pdf`;
      a.click();
    } catch (error) {
      console.error("Error generating Stock Card PDF:", error);
      setFailMessage("Error processing Stock Card PDF");
    }

    setLoading(false);
  };
//#endregion






//#region Access Template
  const accountData = JSON.parse(localStorage.getItem("accountData"));
  
  if (!accountData?.accessTemplate?.hasStockCardReport) {
    return <NoAccess />;
  }
//#endregion





//#region UI
  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <HistoryEduIcon fontSize="large" className="text-theme-color" />
              {`Stock Card Report`}
            </h2>
            <p className="text-gray-500 mt-1">
              Complete movement and running balance of a particular inventory item over a period.
            </p>

            {/* Notice Note */}
            <div className="mt-3 inline-flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <svg
                className="w-4 h-4 text-amber-600 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <span>
                <strong className="font-semibold">Note:</strong> Make sure your Item Inventory stock is synced in this system before generating this report.
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="max-w-2xl">
            <label className="block text-sm font-semibold mb-1 text-gray-700">
              Item
            </label>
            <div className="flex flex-row items-center">
              <input
                type="text"
                placeholder="Select Item"
                className="w-full border border-gray-300 text-sm rounded-l-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color transition"
                value={`${item ? `${item?.itemCode} - ${item?.itemDescription} ${item?.brandName ? `(${item.brandName})` : ''}` : ""}`}
                readOnly
              />
              {!item ?
                <button className="bg-theme-color hover:bg-theme-color-h p-[5px] rounded-r-lg" onClick={openItemSelectForm}>
                  <PersonSearchIcon className="text-white"/>
                </button> 
                :
                <button className="bg-red-400 hover:bg-red-500 p-[5px] rounded-r-lg" onClick={()=>setItem(null)}>
                  <RemoveCircleIcon className="text-white"/>
                </button>
              }
            </div>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Company <span className="text-red-500">*</span>
            </label>
            <CompanySelect data={company} setData={setCompany} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Date From <span className="text-red-500">*</span>
            </label>
            <DateSelect data={dateFrom} setData={setDateFrom} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Date To <span className="text-red-500">*</span>
            </label>
            <DateSelect data={dateTo} setData={setDateTo} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Category
            </label>
            <CategorySelect data={categoryName} setData={setCategoryName} isEditable={true}/>
          </div>
        </div>

        <button onClick={generateHandleClick} className="mt-10 flex items-center gap-2 bg-theme-color text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-theme-color-h transition shadow-md">
          <RestartAltIcon fontSize="small" /> Generate
        </button>
      </div>
      
            
      <ItemSelect 
        visible={showItemSelectForm} 
        onClose={closeItemSelectForm}
        modalName={"Select Item"}
        data={item} setData={setItem}
      />

      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <SuccessMessage message={successMessage} onClose={() => setSuccessMessage("")} />
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>

      { loading && <Loading />}
    </>
  );
//#endregion
}